import { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Form, Modal, Skeleton, Switch } from 'antd';
import { isEmpty } from 'lodash';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import {
  useActiveOrInactivateGroup,
  useDeleteGroup,
  useGroup,
  useUpdateGroup,
} from '@/core/domain/operational/Groups/Groups.hooks';

import { PageProps } from '@/types/PageProps';

import TabContentWrapper from '@/components/_common/TabContentWrapper';
import { SaveButton } from '@/components/_common/Buttons';

import GroupsGeneralForm from '@/components/Operational/Inventory/Groups/GroupsGeneralForm';
import SubgroupsModule from '@/components/Operational/Inventory/Groups/SubgroupsModule';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import GroupFooter from '@/components/Operational/Inventory/Groups/GroupFooter';
import {
  GroupFormValues,
  UpdateGroupRequest,
} from '@/core/domain/operational/Groups/Groups.types';
import handleFormChange from '@/hooks/handleFormChange';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const GroupDetailsPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const history = useHistory();
  const [form] = Form.useForm<GroupFormValues>();
  const { setParamLabel } = useBreadcrumbs();
  const { groupId } = useRouteMatch<{ groupId: string }>().params;
  const [active, setActive] = useState<boolean>(false);
  const [formChange, setFormChange] = useState({});
  const disabled = !Object.keys(formChange).some((key) => !!key);

  const { data: group, isLoading } = useGroup(+groupId);

  const updateMutation = useUpdateGroup();
  const deleteMutation = useDeleteGroup();
  const activateOrInactivateMutation = useActiveOrInactivateGroup();

  useEffect(() => {
    if (group && !isEmpty(group)) {
      form.setFieldsValue({
        name: group.name,
        subAreaId: group.subArea?.id,
      });

      setParamLabel(':groupId', group.name);

      setActive(group.active);
    }
  }, [group]);

  const handleSaveClick = async () => {
    try {
      const values = await form.validateFields();

      const obj: UpdateGroupRequest = {
        ...values,
        id: Number(groupId),
        active,
      };

      await updateMutation.mutateAsync(obj);
    } catch {}
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'groups.remove.modal.title' }),
      content: `${intl.formatMessage({
        id: 'groups.remove.modal.content',
      })}`,
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(+groupId);

          history.push('/app/operational/inventory/groups');
        } catch {}
      },
      okText: intl.formatMessage({
        id: 'groups.remove.modal.button.ok',
      }),
      okType: 'danger',
    });
  };

  const handleActiveClick = async (active: boolean) => {
    try {
      activateOrInactivateMutation.mutateAsync(Number(groupId));

      setActive(active);
    } catch {}
  };

  return (
    <>
      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <SpaceForButton>
            <Switch
              unCheckedChildren={intl.formatMessage({
                id: 'commons.entity.inactive',
              })}
              checkedChildren={intl.formatMessage({
                id: 'commons.entity.active',
              })}
              loading={isLoading}
              checked={active}
              onChange={handleActiveClick}
              disabled={!hasPermissions([Authorities.REGISTER_GROUP_EDIT])}
            />

            <Button
              block
              danger
              onClick={handleRemoveClick}
              loading={deleteMutation.isLoading}
              disabled={!hasPermissions([Authorities.REGISTER_GROUP_DELETE])}
            >
              <FormattedMessage id="commons.form.button.remove" />
            </Button>

            <SaveButton
              block
              type="primary"
              onClick={handleSaveClick}
              loading={updateMutation.isLoading}
              disabled={!hasPermissions([Authorities.REGISTER_GROUP_EDIT])}
            >
              <FormattedMessage id="commons.form.button.save" />
            </SaveButton>
          </SpaceForButton>
        }
      >
        <Skeleton loading={isLoading}>
          <Form.Provider
            onFormChange={(name, info) =>
              handleFormChange(name, info, setFormChange)
            }
          >
            <GroupsGeneralForm group={group} form={form} />
          </Form.Provider>

          {hasPermissions([Authorities.REGISTER_SUBGROUP_VIEW]) && (
            <SubgroupsModule />
          )}

          <GroupFooter disabled={disabled} />
        </Skeleton>
      </TabContentWrapper>
    </>
  );
};

export default GroupDetailsPage;
